/**
 * useSessionPersistenceSync — Two-way bridge between sessionPersistence
 * (saved conversation) and the runtime workflow state.
 *
 * Extracted from WorkflowMachineContext.jsx during Story 4.6 wrap-up.
 * Wraps two useEffects that previously lived in the residue:
 *   1. Restore: when `activeConversationId` changes, hydrate workflow
 *      state, sessionData, backendSession, navigation, and revision
 *      state from the saved conversation.
 *   2. Save: whenever any of the synced slices change, write the
 *      consolidated snapshot back to `updateActiveConversation` (silent
 *      auto-save — does not bump the timestamp).
 *
 * Body lifted verbatim — no logic changes.
 */
import { useEffect } from 'react';
import { ACTIONS } from '../contexts/WorkflowMachineContext';

export function useSessionPersistenceSync({
  sessionPersistence,
  state,
  workflowSession,
  workflowResults,
  stepNavigation,
  revisionWorkflow,
  backendServiceRef,
  dispatch,
}) {
  // Sync with session persistence — restore from saved conversation
  useEffect(() => {
    if (sessionPersistence.activeConversation) {
      // Restore workflow state from saved conversation
      const saved = sessionPersistence.activeConversation;

      if (saved.workflowState) {
        dispatch({
          type: ACTIONS.SET_WORKFLOW_STATE,
          payload: {
            state: saved.workflowState,
            context: saved.workflowContext
          }
        });
      }

      if (saved.sessionData) {
        dispatch({
          type: ACTIONS.UPDATE_SESSION_DATA,
          payload: saved.sessionData
        });
      }

      if (saved.backendSession) {
        // Story 4.2: SET_BACKEND_SESSION migrated to WorkflowSessionContext.
        // Wholesale replace (no merge) — preserves pre-split semantics.
        workflowSession.setBackendSession(saved.backendSession);

        // Restore backend service state
        if (saved.backendSession.threadId) {
          backendServiceRef.current.threadId = saved.backendSession.threadId;
        }
        if (saved.backendSession.fileId) {
          backendServiceRef.current.fileId = saved.backendSession.fileId;
        }
      }

      // Restore navigation state
      if (saved.navigationState) {
        stepNavigation.restoreState(saved.navigationState);
      }

      // Restore revision state
      if (saved.revisionState) {
        revisionWorkflow.restoreState(saved.revisionState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionPersistence.activeConversationId]); // React to conversation changes

  // Save state changes to persistence
  useEffect(() => {
    if (sessionPersistence.activeConversationId) {
      sessionPersistence.updateActiveConversation({
        workflowState: state.workflowState,
        workflowContext: state.workflowContext,
        // Story 4.4: sessionData sourced from WorkflowResultsContext.
        sessionData: workflowResults.sessionData,
        // Story 4.2: backendSession sourced from WorkflowSessionContext.
        backendSession: workflowSession.backendSession,
        navigationState: stepNavigation.getState(),
        revisionState: revisionWorkflow.getState()
      }, false); // Don't update timestamp for automatic saves
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, workflowResults.sessionData, workflowSession.backendSession, stepNavigation.currentStep, revisionWorkflow.revisionHistory]);
}
